import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { LogOut, RefreshCw, Inbox, Star, KeyRound } from "lucide-react";
import Seo from "@/components/Seo";
import { Reveal } from "@/components/Reveal";
import { LogoMark } from "@/components/Logo";
import QuoteCard from "@/components/admin/QuoteCard";
import ReviewCard from "@/components/admin/ReviewCard";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Admin = () => {
  const [auth, setAuth] = useState(null); // null=checking, false=logged out, object=logged in
  const [quotes, setQuotes] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [pwForm, setPwForm] = useState({ current: "", next: "", confirm: "" });
  const [pwError, setPwError] = useState("");
  const [pwLoading, setPwLoading] = useState(false);

  const loadQuotes = async () => {
    const { data } = await axios.get(`${API}/admin/quotes`, { withCredentials: true });
    setQuotes(data.quotes);
  };

  const loadReviews = async () => {
    const { data } = await axios.get(`${API}/admin/reviews`, { withCredentials: true });
    setReviews(data.reviews);
  };

  const loadAll = async () => {
    await Promise.all([loadQuotes(), loadReviews()]);
  };

  useEffect(() => {
    axios
      .get(`${API}/auth/me`, { withCredentials: true })
      .then(async ({ data }) => {
        setAuth(data);
        await loadAll();
      })
      .catch(() => setAuth(false));
  }, []);

  const login = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password }, { withCredentials: true });
      setAuth(data);
      await loadAll();
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Login failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    setAuth(false);
    setQuotes([]);
    setReviews([]);
    toast.success("Logged out.");
  };

  const changePassword = async (e) => {
    e.preventDefault();
    setPwError("");
    if (pwForm.next !== pwForm.confirm) {
      setPwError("New passwords don't match.");
      return;
    }
    setPwLoading(true);
    try {
      await axios.post(
        `${API}/auth/change-password`,
        { current_password: pwForm.current, new_password: pwForm.next },
        { withCredentials: true }
      );
      setPwForm({ current: "", next: "", confirm: "" });
      toast.success("Password updated. Use it next time you sign in.");
    } catch (err) {
      const detail = err.response?.data?.detail;
      setPwError(
        typeof detail === "string"
          ? detail
          : Array.isArray(detail)
            ? detail.map((d) => d.msg).join(" ")
            : "Couldn't update the password. Please try again."
      );
    } finally {
      setPwLoading(false);
    }
  };

  const inputCls =
    "w-full bg-white border border-[#E2DACB] px-4 py-3 text-sm text-[#233D35] focus:border-[#C58A47] focus:outline-none transition-colors";

  return (
    <div className="min-h-[70vh] py-12 sm:py-16" data-testid="admin-page">
      <Seo title="Admin | Good Work Web Co." description="Quote enquiries dashboard for Good Work Web Co." />
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        {auth === null ? null : auth === false ? (
          <Reveal className="max-w-md mx-auto">
            <form onSubmit={login} className="bg-white border border-[#E2DACB] shadow-[0_4px_20px_rgba(35,61,53,0.06)] p-8" data-testid="admin-login-form">
              <div className="flex justify-center"><LogoMark size={44} /></div>
              <h1 className="font-head font-bold uppercase tracking-tight text-2xl text-[#233D35] text-center mt-4">Owner Login</h1>
              <p className="text-sm text-[#77786F] text-center mt-1">Sign in to view quote enquiries.</p>
              {error && (
                <p className="mt-4 text-sm text-red-700 bg-red-50 border border-red-200 px-3 py-2" data-testid="admin-login-error">{error}</p>
              )}
              <label htmlFor="a-email" className="block text-sm font-semibold text-[#233D35] mt-6 mb-1.5">Email</label>
              <input id="a-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="admin-email"
                className="w-full bg-white border border-[#E2DACB] px-4 py-3 text-sm focus:border-[#C58A47] focus:outline-none" />
              <label htmlFor="a-pass" className="block text-sm font-semibold text-[#233D35] mt-4 mb-1.5">Password</label>
              <input id="a-pass" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} data-testid="admin-password"
                className="w-full bg-white border border-[#E2DACB] px-4 py-3 text-sm focus:border-[#C58A47] focus:outline-none" />
              <button type="submit" disabled={loading} data-testid="admin-login-submit"
                className="mt-6 w-full bg-[#233D35] hover:bg-[#1A2621] disabled:opacity-60 text-[#F3EFE5] font-head font-bold uppercase tracking-[0.12em] py-3.5 transition-colors">
                {loading ? "Signing in…" : "Sign In"}
              </button>
            </form>
          </Reveal>
        ) : (
          <div data-testid="admin-dashboard">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div>
                <h1 className="font-head font-bold uppercase tracking-tight text-3xl sm:text-4xl text-[#233D35]">Quote Enquiries</h1>
                <p className="text-sm text-[#77786F] mt-1">Signed in as {auth.email}</p>
              </div>
              <div className="flex gap-3">
                <button onClick={loadAll} data-testid="admin-refresh"
                  className="inline-flex items-center gap-2 border border-[#233D35] text-[#233D35] hover:bg-[#233D35] hover:text-[#F3EFE5] font-head font-bold uppercase tracking-[0.12em] text-sm px-4 py-2.5 transition-colors">
                  <RefreshCw size={15} /> Refresh
                </button>
                <button onClick={logout} data-testid="admin-logout"
                  className="inline-flex items-center gap-2 bg-[#233D35] text-[#F3EFE5] font-head font-bold uppercase tracking-[0.12em] text-sm px-4 py-2.5 hover:bg-[#1A2621] transition-colors">
                  <LogOut size={15} /> Log Out
                </button>
              </div>
            </div>

            {quotes.length === 0 ? (
              <div className="mt-8 bg-white border border-[#E2DACB] p-14 text-center" data-testid="admin-empty">
                <Inbox size={36} className="mx-auto text-[#77786F]" />
                <p className="mt-4 text-[#77786F]">No enquiries yet. They'll show up here as soon as someone sends the form.</p>
              </div>
            ) : (
              <div className="mt-6 space-y-4" data-testid="admin-quotes-list">
                {quotes.map((q) => (
                  <QuoteCard key={q.id} q={q} onChange={loadQuotes} />
                ))}
              </div>
            )}

            <h2 className="font-head font-bold uppercase tracking-tight text-3xl sm:text-4xl text-[#233D35] mt-14">Reviews</h2>
            <p className="text-sm text-[#77786F] mt-1">Approve a review to publish it on the homepage. Delete removes it for good.</p>

            {reviews.length === 0 ? (
              <div className="mt-6 bg-white border border-[#E2DACB] p-14 text-center" data-testid="admin-reviews-empty">
                <Star size={36} className="mx-auto text-[#77786F]" />
                <p className="mt-4 text-[#77786F]">No reviews yet. Submitted reviews will wait here for your approval.</p>
              </div>
            ) : (
              <div className="mt-6 space-y-4" data-testid="admin-reviews-list">
                {reviews.map((r) => (
                  <ReviewCard key={r.id} r={r} onChange={loadReviews} />
                ))}
              </div>
            )}

            <h2 className="font-head font-bold uppercase tracking-tight text-3xl sm:text-4xl text-[#233D35] mt-14">Account</h2>
            <div className="mt-6 bg-white border border-[#E2DACB] p-6 sm:p-8 max-w-lg" data-testid="change-password-card">
              <div className="flex items-center gap-3">
                <KeyRound size={20} className="text-[#C58A47]" />
                <h3 className="font-head font-bold uppercase tracking-tight text-xl text-[#233D35]">Change Password</h3>
              </div>
              <p className="text-sm text-[#77786F] mt-2">At least 8 characters. Your sign-in email stays {auth.email}.</p>
              {pwError && (
                <p className="mt-4 text-sm text-red-700 bg-red-50 border border-red-200 px-3 py-2" data-testid="change-password-error">{pwError}</p>
              )}
              <form onSubmit={changePassword} className="mt-5 space-y-4" data-testid="change-password-form">
                <div>
                  <label htmlFor="pw-current" className="block text-sm font-semibold text-[#233D35] mb-1.5">Current password</label>
                  <input id="pw-current" type="password" required autoComplete="current-password" data-testid="pw-current"
                    className={inputCls} value={pwForm.current} onChange={(e) => setPwForm({ ...pwForm, current: e.target.value })} />
                </div>
                <div>
                  <label htmlFor="pw-new" className="block text-sm font-semibold text-[#233D35] mb-1.5">New password</label>
                  <input id="pw-new" type="password" required minLength={8} autoComplete="new-password" data-testid="pw-new"
                    className={inputCls} value={pwForm.next} onChange={(e) => setPwForm({ ...pwForm, next: e.target.value })} />
                </div>
                <div>
                  <label htmlFor="pw-confirm" className="block text-sm font-semibold text-[#233D35] mb-1.5">Confirm new password</label>
                  <input id="pw-confirm" type="password" required minLength={8} autoComplete="new-password" data-testid="pw-confirm"
                    className={inputCls} value={pwForm.confirm} onChange={(e) => setPwForm({ ...pwForm, confirm: e.target.value })} />
                </div>
                <button type="submit" disabled={pwLoading} data-testid="change-password-submit"
                  className="inline-flex items-center gap-2 bg-[#233D35] hover:bg-[#1A2621] disabled:opacity-60 text-[#F3EFE5] font-head font-bold uppercase tracking-[0.12em] text-sm px-6 py-3 transition-colors">
                  {pwLoading ? "Updating…" : "Update Password"}
                </button>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Admin;
